import { supabase } from './supabase';
import { createBooking, createMobileBooking, deleteBooking, syncMobileBookingsForBooking } from './bookings';
import { cancelBookingAutomations } from '../services/automationCancellationService';

// ─────────────────────────────────────────────────────────────────────────────
// Recurring booking series — booking-level recurrence (replaces memberships).
//
// A booking can be marked recurring at creation with a frequency and a length
// in months. The FIRST booking is created by the normal modal flow (so its
// confirmation + automations fire as usual); this module then fans out the
// remaining occurrences at the same time-of-day, linked via
// bookings.booking_series_id → booking_series.
// ─────────────────────────────────────────────────────────────────────────────

export type SeriesFrequency = 'weekly' | 'biweekly' | 'monthly' | 'quarterly';

export const SERIES_FREQUENCIES: Array<{ value: SeriesFrequency; label: string }> = [
  { value: 'weekly', label: 'Every week' },
  { value: 'biweekly', label: 'Every 2 weeks' },
  { value: 'monthly', label: 'Every month' },
  { value: 'quarterly', label: 'Every 3 months' },
];

export const SERIES_LENGTHS: Array<{ value: number; label: string }> = [
  { value: 3, label: '3 months' },
  { value: 6, label: '6 months' },
  { value: 12, label: '1 year' },
];

export const frequencyLabel = (f: SeriesFrequency): string =>
  SERIES_FREQUENCIES.find(x => x.value === f)?.label || f;

export interface BookingSeries {
  id: string;
  business_id: string;
  customer_id: string | null;
  frequency: SeriesFrequency;
  length_months: number;
  total_visits: number;
  status: 'active' | 'cancelled';
  first_booking_id: string | null;
  mobile_booking: boolean;
  created_at: string;
}

// ── Occurrence generation ────────────────────────────────────────────────────

const pad = (n: number) => String(n).padStart(2, '0');
const toDateStr = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parseDateStr = (s: string): Date => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
};

/** Add months keeping the anchor day-of-month, clamped for short months (Jan 31 → Feb 28). */
const addMonthsClamped = (base: Date, months: number): Date => {
  const anchorDay = base.getDate();
  const d = new Date(base.getFullYear(), base.getMonth() + months, 1);
  const daysInMonth = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  d.setDate(Math.min(anchorDay, daysInMonth));
  return d;
};

/**
 * Occurrence dates strictly after `anchorDate`, up to but not including `boundary`.
 */
export function occurrenceDatesAfter(
  anchorDate: string,
  frequency: SeriesFrequency,
  boundary: Date,
): string[] {
  const first = parseDateStr(anchorDate);
  const dates: string[] = [];

  if (frequency === 'weekly' || frequency === 'biweekly') {
    const stepDays = frequency === 'weekly' ? 7 : 14;
    const cursor = new Date(first);
    for (;;) {
      cursor.setDate(cursor.getDate() + stepDays);
      if (cursor >= boundary) break;
      dates.push(toDateStr(cursor));
    }
  } else {
    const stepMonths = frequency === 'monthly' ? 1 : 3;
    for (let i = 1; ; i++) {
      const next = addMonthsClamped(first, i * stepMonths);
      if (next >= boundary) break;
      dates.push(toDateStr(next));
    }
  }
  return dates;
}

/**
 * All occurrence dates AFTER the first booking, within length_months of it.
 * Same time-of-day is implied — only dates are generated.
 */
export function generateOccurrenceDates(
  firstDate: string, // YYYY-MM-DD
  frequency: SeriesFrequency,
  lengthMonths: number
): string[] {
  return occurrenceDatesAfter(firstDate, frequency, addMonthsClamped(parseDateStr(firstDate), lengthMonths));
}

/** Days between two YYYY-MM-DD dates (b − a). */
const daysBetween = (a: string, b: string): number =>
  Math.round((parseDateStr(b).getTime() - parseDateStr(a).getTime()) / 86400000);

/** Shift the leading YYYY-MM-DD of a date/datetime string by N days, keeping any suffix. */
const shiftDateString = (value: string | undefined, deltaDays: number): string | undefined => {
  if (!value) return value;
  const match = value.match(/^(\d{4}-\d{2}-\d{2})(.*)$/);
  if (!match) return value;
  const shifted = new Date(parseDateStr(match[1]));
  shifted.setDate(shifted.getDate() + deltaDays);
  return `${toDateStr(shifted)}${match[2]}`;
};

/** Deep-clone booking params with every date field shifted by deltaDays. */
const shiftParamsDates = (params: any, deltaDays: number): any => {
  const clone = JSON.parse(JSON.stringify(params));
  clone.start_date = shiftDateString(clone.start_date, deltaDays);
  clone.end_date = shiftDateString(clone.end_date, deltaDays);
  clone.drop_off_date = shiftDateString(clone.drop_off_date, deltaDays);
  for (const vehicle of clone.vehicles || []) {
    for (const service of vehicle.services || []) {
      service.start_date = shiftDateString(service.start_date, deltaDays);
      service.end_date = shiftDateString(service.end_date, deltaDays);
    }
  }
  for (const resource of clone.resources || []) {
    resource.start_date = shiftDateString(resource.start_date, deltaDays);
    resource.end_date = shiftDateString(resource.end_date, deltaDays);
  }
  return clone;
};

// ── Conflict detection ───────────────────────────────────────────────────────

/** Resource ids the booking occupies (main + per-vehicle + explicit rows). */
const collectResourceIds = (params: any): string[] => {
  const ids = new Set<string>();
  if (params.resource_id) ids.add(params.resource_id);
  for (const id of params.resource_ids || []) ids.add(id);
  for (const r of params.resources || []) if (r.resource_id) ids.add(r.resource_id);
  for (const v of params.vehicles || []) if (v.resource_id) ids.add(v.resource_id);
  return Array.from(ids);
};

/**
 * True when any of the booking's resources already has a live booking
 * overlapping the occurrence window. Warning-only — occurrences are created
 * regardless (matches how location bookings allow double-booking today).
 */
async function occurrenceHasConflict(
  resourceIds: string[],
  startIso: string,
  endIso: string
): Promise<boolean> {
  if (!supabase || resourceIds.length === 0) return false;
  try {
    const { data } = await supabase
      .from('booking_resources')
      .select('booking_id, bookings!inner(id, is_deleted, status)')
      .in('resource_id', resourceIds)
      .lt('start_date', endIso)
      .gt('end_date', startIso)
      .eq('bookings.is_deleted', false)
      .neq('bookings.status', 'cancelled')
      .limit(1);
    return (data || []).length > 0;
  } catch (err) {
    console.error('Series conflict check failed (treating as no conflict):', err);
    return false;
  }
}

// ── Series creation / lifecycle ──────────────────────────────────────────────

export interface CreateSeriesResult {
  seriesId: string;
  totalVisits: number;
  /** Child bookings created (excludes the first booking). */
  children: Array<{ id: string; start_date: string }>;
  /** Occurrence dates that overlap an existing booking (created anyway). */
  conflictDates: string[];
  /** Occurrence dates whose creation failed outright. */
  failedDates: string[];
  /** Last occurrence date — for the "visits through …" summary. */
  lastDate: string;
}

/**
 * Fan out a recurring series from an already-created first booking.
 * `baseParams` must be the exact params object used to create that booking.
 */
export async function createBookingSeries(opts: {
  businessId: string;
  customerId: string;
  firstBookingId: string;
  baseParams: any;
  frequency: SeriesFrequency;
  lengthMonths: number;
  isMobile: boolean;
}): Promise<CreateSeriesResult | null> {
  if (!supabase) throw new Error('Supabase client is not initialized');
  const { baseParams } = opts;
  if (!baseParams.start_date || !baseParams.start_time) return null;

  const occurrenceDates = generateOccurrenceDates(baseParams.start_date, opts.frequency, opts.lengthMonths);
  if (occurrenceDates.length === 0) return null;

  const totalVisits = occurrenceDates.length + 1;

  const { data: auth } = await supabase.auth.getUser();
  const { data: series, error: seriesError } = await supabase
    .from('booking_series')
    .insert({
      business_id: opts.businessId,
      customer_id: opts.customerId || null,
      frequency: opts.frequency,
      length_months: opts.lengthMonths,
      total_visits: totalVisits,
      status: 'active',
      first_booking_id: opts.firstBookingId,
      mobile_booking: opts.isMobile,
      created_by: auth?.user?.id ?? null,
    })
    .select('id')
    .single();
  if (seriesError) throw seriesError;

  // Link the first booking into the series.
  await supabase
    .from('bookings')
    .update({ booking_series_id: series.id })
    .eq('id', opts.firstBookingId);

  const resourceIds = collectResourceIds(baseParams);
  const children: CreateSeriesResult['children'] = [];
  const conflictDates: string[] = [];
  const failedDates: string[] = [];

  for (const date of occurrenceDates) {
    const deltaDays = daysBetween(baseParams.start_date, date);
    const childParams = shiftParamsDates(baseParams, deltaDays);
    childParams.booking_series_id = series.id;

    try {
      const startIso = `${childParams.start_date}T${childParams.start_time}`;
      const endIso = `${childParams.end_date || childParams.start_date}T${childParams.end_time || childParams.start_time}`;
      if (await occurrenceHasConflict(resourceIds, startIso, endIso)) {
        conflictDates.push(date);
      }

      const booking = opts.isMobile
        ? await createMobileBooking(childParams)
        : await createBooking(childParams);
      if (booking?.id) {
        children.push({ id: booking.id, start_date: date });
      } else {
        failedDates.push(date);
      }
    } catch (err) {
      console.error(`Series occurrence ${date} failed:`, err);
      failedDates.push(date);
    }
  }

  return {
    seriesId: series.id,
    totalVisits,
    children,
    conflictDates,
    failedDates,
    lastDate: occurrenceDates[occurrenceDates.length - 1],
  };
}

export interface BookingSeriesVisit {
  id: string;
  start_date: string;
  start_time: string;
  status: string;
  booking_number: number | null;
  title: string | null;
  color: string | null;
  secondary_color: string | null;
  customer: { first_name: string | null; last_name: string | null } | null;
}

/** Series + its (non-deleted) bookings, ordered by date. */
export async function getBookingSeries(seriesId: string): Promise<{
  series: BookingSeries;
  bookings: BookingSeriesVisit[];
} | null> {
  if (!supabase) throw new Error('Supabase client is not initialized');
  const { data: series, error } = await supabase
    .from('booking_series')
    .select('*')
    .eq('id', seriesId)
    .maybeSingle();
  if (error) throw error;
  if (!series) return null;

  const { data: bookings } = await supabase
    .from('bookings')
    .select('id, start_date, start_time, status, booking_number, title, color, secondary_color, customer:customers!bookings_customer_id_fkey(first_name, last_name)')
    .eq('booking_series_id', seriesId)
    .eq('is_deleted', false)
    .order('start_date', { ascending: true });

  return {
    series: series as BookingSeries,
    bookings: (bookings || []).map((b: any) => ({
      ...b,
      customer: Array.isArray(b.customer) ? b.customer[0] ?? null : b.customer ?? null,
    })),
  };
}

/**
 * Cancel a series: mark cancelled + soft-delete FUTURE scheduled occurrences
 * (past/completed visits are untouched). Mirrors the old membership cascade.
 */
export async function cancelBookingSeries(seriesId: string): Promise<number> {
  if (!supabase) throw new Error('Supabase client is not initialized');

  const { error: seriesError } = await supabase
    .from('booking_series')
    .update({ status: 'cancelled', updated_at: new Date().toISOString() })
    .eq('id', seriesId);
  if (seriesError) throw seriesError;

  const today = toDateStr(new Date());
  const { data: future, error: fetchError } = await supabase
    .from('bookings')
    .select('id')
    .eq('booking_series_id', seriesId)
    .eq('is_deleted', false)
    .in('status', ['scheduled'])
    .gte('start_date', today);
  if (fetchError) throw fetchError;

  const ids = (future || []).map(b => b.id);
  if (ids.length === 0) return 0;

  // Mobile shadow rows (incl. buffer blocks) are hard-deleted so the calendar
  // frees the slots; the bookings themselves are soft-deleted.
  const { error: mobileError } = await supabase
    .from('mobile_bookings')
    .delete()
    .in('booking_id', ids);
  if (mobileError) console.error('Series cancel: mobile_bookings cleanup failed:', mobileError);

  const { error: bookingsError } = await supabase
    .from('bookings')
    .update({ is_deleted: true, updated_at: new Date().toISOString() })
    .in('id', ids);
  if (bookingsError) throw bookingsError;

  return ids.length;
}

const dateOnly = (value: string | null | undefined): string | undefined =>
  value ? String(value).slice(0, 10) : undefined;

const timeOnly = (value: string | null | undefined): string | undefined =>
  value ? String(value).slice(0, 5) : undefined;

/** Keep booking_series.total_visits equal to the visits that still exist. */
export async function syncSeriesVisitCount(seriesId: string): Promise<number> {
  if (!supabase) throw new Error('Supabase client is not initialized');
  const { count, error } = await supabase
    .from('bookings')
    .select('id', { count: 'exact', head: true })
    .eq('booking_series_id', seriesId)
    .eq('is_deleted', false);
  if (error) throw error;
  const total = count ?? 0;
  const { error: updateError } = await supabase
    .from('booking_series')
    .update({
      total_visits: total,
      ...(total === 0 ? { status: 'cancelled' } : {}),
      updated_at: new Date().toISOString(),
    })
    .eq('id', seriesId);
  if (updateError) throw updateError;
  return total;
}

/** Delete one visit the same way a normal booking is deleted. The rest of the series stays. */
export async function deleteSeriesVisit(seriesId: string, businessId: string, bookingId: string): Promise<void> {
  try {
    await cancelBookingAutomations(bookingId, 'series_visit_deleted', businessId);
  } catch (err) {
    console.error(`Failed to cancel automations for series visit ${bookingId}:`, err);
  }
  await deleteBooking(bookingId);
  await syncSeriesVisitCount(seriesId);
}

async function seriesBoundary(series: BookingSeries, visits: BookingSeriesVisit[]): Promise<Date> {
  let origin = visits[0]?.start_date;
  if (supabase && series.first_booking_id) {
    const { data } = await supabase
      .from('bookings')
      .select('start_date')
      .eq('id', series.first_booking_id)
      .maybeSingle();
    if (data?.start_date) origin = String(data.start_date).slice(0, 10);
  }
  if (!origin) throw new Error('This series has no visits to schedule from.');
  return addMonthsClamped(parseDateStr(origin), series.length_months);
}

async function moveBookingToDate(bookingId: string, newStart: string): Promise<void> {
  if (!supabase) throw new Error('Supabase client is not initialized');
  const { data: booking, error } = await supabase
    .from('bookings')
    .select('start_date, end_date, drop_off_date, mobile_booking')
    .eq('id', bookingId)
    .single();
  if (error) throw error;
  const current = String(booking.start_date).slice(0, 10);
  const delta = daysBetween(current, newStart);
  if (delta === 0) return;

  const { error: updateError } = await supabase
    .from('bookings')
    .update({
      start_date: newStart,
      end_date: booking.end_date ? shiftDateString(String(booking.end_date).slice(0, 10), delta) : null,
      drop_off_date: booking.drop_off_date
        ? shiftDateString(String(booking.drop_off_date).slice(0, 10), delta)
        : null,
      updated_at: new Date().toISOString(),
    })
    .eq('id', bookingId);
  if (updateError) throw updateError;

  const { data: resources } = await supabase
    .from('booking_resources')
    .select('id, start_date, end_date')
    .eq('booking_id', bookingId);
  for (const resource of resources || []) {
    if (!resource.start_date && !resource.end_date) continue;
    await supabase
      .from('booking_resources')
      .update({
        start_date: shiftDateString(resource.start_date, delta),
        end_date: shiftDateString(resource.end_date, delta),
      })
      .eq('id', resource.id);
  }

  const { data: services } = await supabase
    .from('booking_services')
    .select('id, start_date, end_date')
    .eq('booking_id', bookingId);
  for (const service of services || []) {
    if (!service.start_date && !service.end_date) continue;
    await supabase
      .from('booking_services')
      .update({
        start_date: shiftDateString(service.start_date, delta),
        end_date: shiftDateString(service.end_date, delta),
      })
      .eq('id', service.id);
  }

  if (booking.mobile_booking) {
    await syncMobileBookingsForBooking(bookingId);
  }
}

async function cloneParamsFromBooking(bookingId: string): Promise<{ params: any; isMobile: boolean }> {
  if (!supabase) throw new Error('Supabase client is not initialized');
  const { data: booking, error } = await supabase
    .from('bookings')
    .select(`
      business_id, customer_id, title, start_date, start_time, end_date, end_time,
      drop_off_date, drop_off_time, notes, customer_notes, color, secondary_color,
      booking_address, booking_lat, booking_lng, mobile_booking, customer_repeat_status,
      source, created_by, automations_disabled, location
    `)
    .eq('id', bookingId)
    .single();
  if (error || !booking) throw error || new Error('Booking not found');

  const [{ data: services }, { data: resources }, { data: assets }] = await Promise.all([
    supabase
      .from('booking_services')
      .select(`
        service_id, vehicle_id, name, description, price, discount_type, discount_value,
        discount_amount, tax_exempt, tax_rate, duration_minutes, workflow_steps,
        pricing_type, unit_price, duration_per_unit, quantity, compare_at_price
      `)
      .eq('booking_id', bookingId),
    supabase.from('booking_resources').select('resource_id, role').eq('booking_id', bookingId),
    supabase.from('booking_assets').select('asset_id').eq('booking_id', bookingId),
  ]);

  let bufferTime = 0;
  if (booking.mobile_booking) {
    const { data: buffers } = await supabase
      .from('mobile_bookings')
      .select('buffer_time')
      .eq('booking_id', bookingId)
      .eq('is_buffer_block', true)
      .limit(1);
    bufferTime = buffers?.[0]?.buffer_time || 0;
  }

  const byVehicle = new Map<string, any[]>();
  for (const service of services || []) {
    const key = service.vehicle_id || 'no-vehicle';
    const list = byVehicle.get(key) || [];
    list.push({
      service_id: service.service_id,
      name: service.name,
      description: service.description || '',
      price: Number(service.price) || 0,
      discount_type: service.discount_type || 'fixed',
      discount_value: service.discount_value || 0,
      discount_amount: service.discount_amount || 0,
      tax_exempt: service.tax_exempt || false,
      tax_rate: service.tax_rate ?? 5,
      duration_minutes: service.duration_minutes || 0,
      workflow_steps: service.workflow_steps || null,
      pricing_type: service.pricing_type || 'flat',
      unit_price: service.unit_price,
      duration_per_unit: service.duration_per_unit,
      quantity: service.quantity ?? 1,
      compare_at_price: service.compare_at_price,
    });
    byVehicle.set(key, list);
  }

  const resourceIds = (resources || []).map((row) => row.resource_id).filter(Boolean);
  return {
    isMobile: !!booking.mobile_booking,
    params: {
      business_id: booking.business_id,
      customer_id: booking.customer_id,
      title: booking.title,
      start_date: dateOnly(booking.start_date),
      start_time: timeOnly(booking.start_time),
      end_date: dateOnly(booking.end_date),
      end_time: timeOnly(booking.end_time),
      drop_off_date: dateOnly(booking.drop_off_date),
      drop_off_time: timeOnly(booking.drop_off_time),
      status: 'scheduled',
      notes: booking.notes || '',
      customer_notes: booking.customer_notes || '',
      color: booking.color,
      secondary_color: booking.secondary_color || undefined,
      booking_address: booking.booking_address,
      booking_lat: booking.booking_lat,
      booking_lng: booking.booking_lng,
      location: booking.location,
      mobile_booking: booking.mobile_booking,
      customer_repeat_status: booking.customer_repeat_status,
      source: booking.source,
      created_by: booking.created_by,
      automations_disabled: booking.automations_disabled || false,
      buffer_time: bufferTime,
      vehicles: Array.from(byVehicle.entries()).map(([vehicleId, vehicleServices]) => ({
        vehicle_id: vehicleId === 'no-vehicle' ? null : vehicleId,
        services: vehicleServices,
      })),
      resources: (resources || [])
        .filter((row) => row.resource_id)
        .map((row) => ({ resource_id: row.resource_id, role: row.role || 'technician' })),
      resource_ids: resourceIds,
      asset_ids: (assets || []).map((row) => row.asset_id).filter(Boolean),
    },
  };
}

/**
 * Keep the selected visit (moved to `startDate` when that differs) and every
 * visit before it. Later visits that are not completed are deleted, then new
 * visits are created on `frequency` from that date through the original series end.
 */
export async function rescheduleSeriesFromVisit(opts: {
  series: BookingSeries;
  visits: BookingSeriesVisit[];
  anchorBookingId: string;
  frequency: SeriesFrequency;
  startDate: string;
}): Promise<{ removed: number; created: number }> {
  if (!supabase) throw new Error('Supabase client is not initialized');
  const ordered = [...opts.visits].sort((a, b) => a.start_date.localeCompare(b.start_date));
  const anchorIndex = ordered.findIndex((visit) => visit.id === opts.anchorBookingId);
  if (anchorIndex < 0) throw new Error('That visit is no longer in this series.');

  const anchor = ordered[anchorIndex];
  const later = ordered.slice(anchorIndex + 1);
  const boundary = await seriesBoundary(opts.series, ordered);

  await moveBookingToDate(anchor.id, opts.startDate);

  let removed = 0;
  const keptDates = new Set<string>(
    ordered.slice(0, anchorIndex).map((visit) => visit.start_date.slice(0, 10)),
  );
  keptDates.add(opts.startDate);

  for (const visit of later) {
    if (visit.status === 'completed' || visit.status === 'in_progress') {
      keptDates.add(visit.start_date.slice(0, 10));
      continue;
    }
    try {
      await cancelBookingAutomations(visit.id, 'series_rescheduled', opts.series.business_id);
    } catch (err) {
      console.error(`Failed to cancel automations for series visit ${visit.id}:`, err);
    }
    await deleteBooking(visit.id);
    removed += 1;
  }

  const { params, isMobile } = await cloneParamsFromBooking(anchor.id);
  const dates = occurrenceDatesAfter(opts.startDate, opts.frequency, boundary)
    .filter((date) => !keptDates.has(date));

  let created = 0;
  for (const date of dates) {
    const deltaDays = daysBetween(params.start_date, date);
    const childParams = shiftParamsDates(params, deltaDays);
    childParams.booking_series_id = opts.series.id;
    const booking = isMobile
      ? await createMobileBooking(childParams)
      : await createBooking(childParams);
    if (!booking?.id) throw new Error(`Could not create the visit on ${date}.`);
    created += 1;
  }

  const total = await syncSeriesVisitCount(opts.series.id);
  const { error } = await supabase
    .from('booking_series')
    .update({
      frequency: opts.frequency,
      total_visits: total,
      status: total === 0 ? 'cancelled' : 'active',
      updated_at: new Date().toISOString(),
    })
    .eq('id', opts.series.id);
  if (error) throw error;

  return { removed, created };
}
